import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { blogAPI } from '../utils/api';
import SEO from '../components/SEO';
import config from '../config/config';
import './Blog.css';
import artistImage from '../assets/images/banner.png';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      const response = await blogAPI.getAll(params);
      const allPosts = response.data;
      setPosts(allPosts);
      // Set first featured post as hero
      const featured = allPosts.find(p => p.featured);
      setFeaturedPost(featured || allPosts[0] || null);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await blogAPI.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const calculateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const words = content?.split(/\s+/).length || 0;
    return Math.ceil(words / wordsPerMinute);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const filteredPosts = searchQuery
    ? posts.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : posts;

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Canvaart Art Blog",
    "description": "Discover art tips, painting guides, artist stories, and home decor inspiration from Aanishka Chowdhury.",
    "url": "https://canvaart.in/blog",
    "publisher": {
      "@type": "Person",
      "name": "Aanishka Chowdhury"
    }
  };

  return (
    <div className="blog-page">
      <SEO
        title="Art Blog"
        description="Discover art tips, painting guides, artist stories, and home decor inspiration from Aanishka Chowdhury. Learn how to choose, commission, and care for original canvas paintings."
        keywords="art blog, painting tips, canvas art guide, home decor art, artist stories, custom painting tips, art buying guide, Indian artist blog"
        url="/blog"
        schema={blogSchema}
      />

      {/* Hero Section with Featured Post */}
      {featuredPost && (
        <section className="blog-hero" aria-label="Featured blog post">
          <div className="container">
            <motion.div
              className="blog-hero-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <Link to={`/blog/${featuredPost.slug}`} className="blog-hero-link">
                <div className="blog-hero-image">
                  <img
                    src={featuredPost.image_url ? `${config.BASE_API_URL}${featuredPost.image_url}` : {artistImage}}
                    alt={featuredPost.title}
                    loading="eager"
                  />
                  <div className="blog-hero-overlay">
                    <span className="blog-hero-badge">Featured</span>
                  </div>
                </div>
                <div className="blog-hero-text">
                  <span className="blog-hero-category">{featuredPost.category}</span>
                  <h1>{featuredPost.title}</h1>
                  <p className="blog-hero-excerpt">{featuredPost.excerpt}</p>
                  <div className="blog-hero-meta">
                    <span>{formatDate(featuredPost.created_at)}</span>
                    <span className="blog-meta-dot">•</span>
                    <span>{calculateReadingTime(featuredPost.content)} min read</span>
                    <span className="blog-meta-dot">•</span>
                    <span>By {featuredPost.author}</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      <div className="container blog-layout">
        {/* Sidebar */}
        <aside className="blog-sidebar" aria-label="Blog filters">
          {/* Search */}
          <div className="sidebar-section">
            <h3>Search Articles</h3>
            <div className="blog-search">
              <input
                type="text"
                placeholder="Search blog posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search blog posts"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="sidebar-section">
            <h3>Categories</h3>
            <nav className="blog-categories" aria-label="Blog categories">
              <button
                className={`category-btn ${!selectedCategory ? 'active' : ''}`}
                onClick={() => setSelectedCategory('')}
                aria-pressed={!selectedCategory}
              >
                All Posts
                <span className="category-count">{posts.length}</span>
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.category}
                  className={`category-btn ${selectedCategory === cat.category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.category)}
                  aria-pressed={selectedCategory === cat.category}
                >
                  {cat.category}
                  <span className="category-count">{cat.count}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* About Author Widget */}
          <div className="sidebar-section author-widget">
            <h3>About the Author</h3>
            <div className="author-widget-content">
              <img
                src={artistImage}
                alt="Aanishka Chowdhury"
                className="author-widget-image"
              />
              <p className="author-widget-text">
                Aanishka Chowdhury is a professional canvas artist from India specializing in abstract art, portraits, and custom commissions.
              </p>
              <Link to="/about" className="author-widget-link">Read Full Bio</Link>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="blog-main">
          <section aria-label="Blog posts">
            <div className="blog-section-header">
              <h2>
                {selectedCategory ? `${selectedCategory} Articles` : 'Latest Articles'}
              </h2>
              <span className="blog-post-count">{filteredPosts.length} posts</span>
            </div>

            {loading ? (
              <div className="spinner" role="status" aria-label="Loading blog posts"></div>
            ) : filteredPosts.length === 0 ? (
              <div className="no-posts">
                <p>No articles found.</p>
                {searchQuery && <p>Try a different search term.</p>}
              </div>
            ) : (
              <div className="blog-grid" role="list">
                {filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    className="blog-card"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    viewport={{ once: true }}
                    role="listitem"
                    itemScope
                    itemType="https://schema.org/BlogPosting"
                  >
                    <Link to={`/blog/${post.slug}`} className="blog-card-link">
                      <div className="blog-card-image">
                        <img
                          src={post.image_url ? `${config.BASE_API_URL}${post.image_url}` : {artistImage}}
                          alt={post.title}
                          loading="lazy"
                          itemProp="image"
                        />
                        {post.category && (
                          <span className="blog-card-category">{post.category}</span>
                        )}
                      </div>
                      <div className="blog-card-content">
                        <h3 itemProp="headline">{post.title}</h3>
                        <p className="blog-card-excerpt" itemProp="description">{post.excerpt}</p>
                        <div className="blog-card-meta">
                          <span>{formatDate(post.created_at)}</span>
                          <span className="blog-meta-dot">•</span>
                          <span>{calculateReadingTime(post.content)} min read</span>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default Blog;
